import { navigationHistoryService } from './navigationHistoryService';

/**
 * Known user-specific localStorage keys to remove on logout.
 * Terminal layout keys are intentionally excluded — they have no Firestore
 * backup so clearing them would permanently destroy the user's workspace.
 */
const STATIC_KEYS_TO_CLEAR = [
  // VerdéScore
  'vdk-verdescore-history',
  'vdk-verdescore-starred',

  // LitigationIQ
  'litigationIQ_conversation_history',
  'litigationIQ_job_message_map',
  'litigationIQ_documents_metadata',
  'litigationIQ_document_filters',
  'litigationIQ_document_view_preferences',
  'litigationIQ_show_templates',
  'litigationIQ_library_view',

  // Marketplace
  'vdk-marketplace-wallet',

  // Notification settings
  'vdk-notification-settings',
  'vdk-browser-notification-settings',
  'vim_notifications_enabled',
  'vim_notification_sound',
];

/**
 * Prefixes to scrub as a safety net for any dynamically-named keys.
 * Keys matching these prefixes will be removed UNLESS they match
 * the preserve list (terminal layout keys).
 */
const SCRUB_PREFIXES = [
  'vdk-verdescore-',
  'litigationIQ_',
  'vdk-marketplace-',
  'vim_',
  'vdk-notification-',
  'vdk-browser-notification-',
  'vdk-jobs:',
];

/** Prefixes that must NEVER be cleared (no Firestore backup). */
const PRESERVE_PREFIXES = [
  'vdk-terminal-',
  'vdk-layout-',
];

function shouldPreserve(key: string): boolean {
  return PRESERVE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Remove all user-specific client state on logout.
 *
 * @param uid - The UID of the user being logged out, used to clear
 *              UID-scoped keys like `vdk-jobs:{uid}`.
 */
export function cleanupUserSession(uid: string): void {
  // 1. Remove known static keys
  STATIC_KEYS_TO_CLEAR.forEach((key) => localStorage.removeItem(key));

  // 2. Remove UID-scoped keys
  if (uid) {
    localStorage.removeItem(`vdk-jobs:${uid}`);
  }

  // 3. Prefix scrub — catch any keys we may have missed
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (shouldPreserve(key)) continue;
    if (SCRUB_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // 4. Clear all sessionStorage (only contains transient auth flow data)
  sessionStorage.clear();

  // 5. Clear in-memory navigation history
  navigationHistoryService.clear();
}
